import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Save, X } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';

// PR-D1 — Dumpers clients & per-ton rates. Lists dumper-scoped clients with
// their active contract (rate per ton + payment term in days) and lets the
// operator add/edit them. The rate/term are inputs to LATER computation
// (revenue, invoice due date); nothing is invoiced here.

interface Row {
  client_id: string;
  client_code: string;
  name_en: string;
  name_ar: string | null;
  client_status: string;
  contract_id: string | null;
  contract_code: string | null;
  rate_per_ton_sar: string | null;
  payment_term_days: number | null;
  load_type: string | null;
  effective_from: string | null;
  contract_status: string | null;
}

const inputCls = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm';

export default function DumperClientsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['dumper-contracts'],
    queryFn: async () => (await apiClient.get<{ items: Row[] }>('/api/v1/dumper-contracts')).data.items,
  });

  // --- add new client + rate ---
  const [name, setName] = useState('');
  const [rate, setRate] = useState('');
  const [term, setTerm] = useState('');
  const [loadType, setLoadType] = useState('');

  const addMut = useMutation({
    mutationFn: async () =>
      apiClient.post('/api/v1/dumper-contracts', {
        newClientNameEn: name.trim(),
        ratePerTonSar: Number(rate || 0),
        paymentTermDays: Number(term || 0),
        loadType: loadType.trim() || null,
      }),
    onSuccess: () => {
      setName(''); setRate(''); setTerm(''); setLoadType('');
      qc.invalidateQueries({ queryKey: ['dumper-contracts'] });
    },
  });

  // --- inline edit of a row's rate/term ---
  const [editId, setEditId] = useState<string | null>(null);
  const [eRate, setERate] = useState('');
  const [eTerm, setETerm] = useState('');
  const [eLoad, setELoad] = useState('');

  function startEdit(r: Row) {
    setEditId(r.client_id);
    setERate(r.rate_per_ton_sar ?? '');
    setETerm(r.payment_term_days != null ? String(r.payment_term_days) : '');
    setELoad(r.load_type ?? '');
  }

  const saveMut = useMutation({
    mutationFn: async (r: Row) => {
      const body = {
        ratePerTonSar: Number(eRate || 0),
        paymentTermDays: Number(eTerm || 0),
        loadType: eLoad.trim() || null,
      };
      if (r.contract_id) return apiClient.patch(`/api/v1/dumper-contracts/${r.contract_id}`, body);
      return apiClient.post('/api/v1/dumper-contracts', { clientId: r.client_id, ...body });
    },
    onSuccess: () => {
      setEditId(null);
      qc.invalidateQueries({ queryKey: ['dumper-contracts'] });
    },
  });

  const rows = data ?? [];

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg text-foreground">Dumpers · Clients & rates</div>
          <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard'; }} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> <span>Dumpers home</span>
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-8">
        {/* Add new */}
        <div className="rounded-xl border border-border bg-card p-4 mb-6">
          <div className="text-sm font-medium mb-3">Add a client</div>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
            <div className="sm:col-span-2">
              <label className="text-xs text-muted-foreground">Client name</label>
              <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ARASCO" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Rate / ton (SAR)</label>
              <input className={inputCls} value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal" placeholder="0" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Term (days)</label>
              <input className={inputCls} value={term} onChange={(e) => setTerm(e.target.value)} inputMode="numeric" placeholder="0" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Load type (optional)</label>
              <input className={inputCls} value={loadType} onChange={(e) => setLoadType(e.target.value)} placeholder="sand…" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button size="sm" disabled={name.trim().length < 2 || addMut.isPending} onClick={() => addMut.mutate()} className="gap-2">
              <Plus className="h-4 w-4" /> Add client
            </Button>
            {addMut.isError && <span className="text-xs text-rose-500">Could not save. Check the inputs.</span>}
          </div>
        </div>

        {/* List */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr className="text-start">
                <th className="text-start font-medium px-4 py-2">Client</th>
                <th className="text-start font-medium px-4 py-2">Load type</th>
                <th className="text-end font-medium px-4 py-2">Rate / ton</th>
                <th className="text-end font-medium px-4 py-2">Term (days)</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No dumper clients yet. Add one above.</td></tr>
              )}
              {rows.map((r) => {
                const editing = editId === r.client_id;
                return (
                  <tr key={r.client_id} className="border-t border-border">
                    <td className="px-4 py-2">
                      <div className="font-medium">{r.name_en}</div>
                      <div className="text-xs text-muted-foreground font-mono">{r.client_code}</div>
                    </td>
                    <td className="px-4 py-2">
                      {editing
                        ? <input className={inputCls} value={eLoad} onChange={(e) => setELoad(e.target.value)} />
                        : (r.load_type ?? <span className="text-muted-foreground">—</span>)}
                    </td>
                    <td className="px-4 py-2 text-end">
                      {editing
                        ? <input className={`${inputCls} text-end`} value={eRate} onChange={(e) => setERate(e.target.value)} inputMode="decimal" />
                        : (r.rate_per_ton_sar != null ? `${Number(r.rate_per_ton_sar).toLocaleString()} SAR` : <span className="text-amber-500">not set</span>)}
                    </td>
                    <td className="px-4 py-2 text-end">
                      {editing
                        ? <input className={`${inputCls} text-end`} value={eTerm} onChange={(e) => setETerm(e.target.value)} inputMode="numeric" />
                        : (r.payment_term_days != null ? r.payment_term_days : <span className="text-amber-500">not set</span>)}
                    </td>
                    <td className="px-4 py-2 text-end whitespace-nowrap">
                      {editing ? (
                        <div className="inline-flex gap-1">
                          <Button size="sm" className="gap-1" disabled={saveMut.isPending} onClick={() => saveMut.mutate(r)}>
                            <Save className="h-3.5 w-3.5" /> Save
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditId(null)}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => startEdit(r)}>Edit rate / term</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted-foreground mt-4">
          Rates and terms feed trip revenue and invoice due dates later. Nothing here is invoiced or sent.
        </p>
      </main>
    </div>
  );
}
